import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CATEGORIES } from '../mock';
import { HorizontalCard, SmartImage } from '../components/ArticleCard';
import SectionTitle from '../components/SectionTitle';
import SEO from '../components/SEO';
import { useStories, normaliseStories } from '../hooks/useStories';

// Petit item de sidebar avec image
const SidebarStoryItem = ({ post, showRank, rank }) => (
  <article className="group grid grid-cols-[70px_1fr] gap-3">
    <Link to={`/article/${post.slug}`} className="relative block aspect-square overflow-hidden bg-[#efe8de]">
      <SmartImage src={post.image} alt={post.title} hover={false} />
      {showRank && (
        <span className="absolute top-1 left-1 bg-[#1a1414]/90 text-[#c9a961] text-[10px] font-bold w-5 h-5 flex items-center justify-center">
          {rank}
        </span>
      )}
    </Link>
    <div>
      <span className="text-[9px] uppercase tracking-[0.18em] text-[#8b2c3f] font-bold">
        {post.category}
      </span>
      <h4
        className="mt-1 text-[13px] leading-snug font-bold text-[#1a1414] group-hover:text-[#8b2c3f] transition-colors line-clamp-3"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        <Link to={`/article/${post.slug}`}>{post.title}</Link>
      </h4>
    </div>
  </article>
);

// Utilise un tirage pseudo-aléatoire stable par session (change à chaque rechargement)
const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const RowSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-5 border-b border-[#e6ddd0] pb-6 animate-pulse">
    <div className="aspect-[16/10] bg-[#efe8de]" />
    <div className="space-y-3">
      <div className="h-3 w-32 bg-[#efe8de]" />
      <div className="h-5 w-full bg-[#efe8de]" />
      <div className="h-4 w-3/4 bg-[#efe8de]" />
    </div>
  </div>
);

const Category = () => {
  const { slug } = useParams();
  const knownCategory = CATEGORIES.find((c) => c.slug === slug);
  const catName = knownCategory?.name || (slug ? slug.replace(/-/g, ' ') : 'Catégorie');
  const isUnknownCategory = !knownCategory;

  // Histoires de la catégorie courante (colonne principale)
  const { data: raw, loading, error } = useStories({ category: slug, limit: 100, order: 'desc' });
  const list = normaliseStories(raw);

  // Toutes les histoires (pour alimenter la sidebar avec du contenu réel)
  const { data: allRaw } = useStories({ limit: 100, order: 'desc' });
  const allStories = normaliseStories(allRaw);

  // Tri aléatoire pour la sidebar → change à chaque rechargement / navigation
  // useMemo garantit qu'on ne re-shuffle pas à chaque re-render dans la même page
  const [seed] = useState(() => Date.now());
  const sidebarLesPlusLus = useMemo(() => {
    // Les 5 plus récentes (semble "les plus lues" — dynamique)
    return allStories.slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStories.length, seed]);

  const sidebarCoupsDeCoeur = useMemo(() => {
    // Sélection "surprise" aléatoire parmi toutes les histoires, hors sidebarLesPlusLus
    const excluded = new Set(sidebarLesPlusLus.map((s) => s.id));
    const pool = allStories.filter((s) => !excluded.has(s.id));
    return shuffleArray(pool).slice(0, 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStories.length, seed]);

  return (
    <main className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
      <SEO
        title={catName}
        description={`Découvrez tous les récits de la rubrique ${catName} sur BcoolStore.`}
        url={`/category/${slug}`}
        noIndex={isUnknownCategory}
      />
      <div className="lg:col-span-2">
        <div className="mb-8">
          <div className="text-[12px] uppercase tracking-widest text-[#7a716c]">Catégorie</div>
          <h1
            className="mt-1 text-3xl md:text-4xl font-black capitalize text-[#1a1414]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {catName}
          </h1>
          <div className="h-[3px] w-14 bg-[#c9a961] mt-3" />
          {!loading && list.length > 0 && (
            <p className="mt-3 text-[13px] text-[#7a716c]">
              {list.length} histoire{list.length > 1 ? 's' : ''} publiée{list.length > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {error && (
          <p className="text-[#8b2c3f]">Impossible de charger les articles. Rafraîchissez la page.</p>
        )}

        <div className="space-y-6">
          {loading && !list.length ? (
            Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)
          ) : list.length === 0 ? (
            <p className="text-[#7a716c] italic"
               style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Aucune histoire dans cette catégorie pour l&apos;instant.
            </p>
          ) : (
            list.map((p) => <HorizontalCard key={p.id} post={p} />)
          )}
        </div>
      </div>

      <aside>
        <SectionTitle>LES PLUS LUS</SectionTitle>
        <div className="space-y-6">
          {sidebarLesPlusLus.length === 0 ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="grid grid-cols-[70px_1fr] gap-3 animate-pulse">
                <div className="aspect-square bg-[#efe8de]" />
                <div className="space-y-2">
                  <div className="h-3 w-full bg-[#efe8de]" />
                  <div className="h-3 w-3/4 bg-[#efe8de]" />
                </div>
              </div>
            ))
          ) : (
            sidebarLesPlusLus.map((p, i) => (
              <SidebarStoryItem key={p.id} post={p} showRank rank={i + 1} />
            ))
          )}
        </div>

        <div className="mt-10">
          <SectionTitle>NOS COUPS DE CŒUR</SectionTitle>
          <div className="space-y-5">
            {sidebarCoupsDeCoeur.length === 0 ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="grid grid-cols-[70px_1fr] gap-3 animate-pulse">
                  <div className="aspect-square bg-[#efe8de]" />
                  <div className="space-y-2">
                    <div className="h-3 w-full bg-[#efe8de]" />
                    <div className="h-3 w-3/4 bg-[#efe8de]" />
                  </div>
                </div>
              ))
            ) : (
              sidebarCoupsDeCoeur.map((p) => <SidebarStoryItem key={p.id} post={p} />)
            )}
          </div>
        </div>
      </aside>
    </main>
  );
};

export default Category;
